"use client";

import { useEffect } from "react";
import { Moon, Sparkles, LineChart, Baby } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChildProfileDialog } from "@/components/child-profile-dialog";
import type { Child } from "@/lib/types";
import { trackEvent, trackScreenView, AnalyticsEvents } from "@/lib/analytics";

interface OnboardingWelcomeProps {
    onSave: (child: Omit<Child, 'id' | 'userId'> | Child) => void;
}

export function OnboardingWelcome({ onSave }: OnboardingWelcomeProps) {
    // Track onboarding start
    useEffect(() => {
        trackScreenView('Onboarding');
        trackEvent(AnalyticsEvents.ONBOARDING_START);
    }, []);

    return (
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 animate-in fade-in duration-700 slide-in-from-bottom-4">
            <div className="text-center space-y-4 mb-8 max-w-md">
                <div className="flex justify-center mb-6">
                    <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <Moon className="h-8 w-8 text-primary" />
                    </div>
                </div>
                <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-br from-foreground to-muted-foreground bg-clip-text text-transparent">
                    Welcome to LullabAI
                </h1>
                <p className="text-muted-foreground text-lg">
                    Your personal AI-powered sleep coach. Let's get to know your little one to start the journey to better sleep.
                </p>
            </div>

            <div className="grid gap-4 w-full max-w-sm mb-8">
                <FeatureItem
                    icon={<LineChart className="h-5 w-5 text-blue-500" />}
                    title="Track Sleep"
                    description="Log naps and night sleep with ease"
                />
                <FeatureItem
                    icon={<Sparkles className="h-5 w-5 text-amber-500" />}
                    title="Get AI Insights"
                    description="Personalized coaching based on real data"
                />
                <FeatureItem
                    icon={<Baby className="h-5 w-5 text-purple-500" />}
                    title="Find Your Rhythm"
                    description="Discover the perfect schedule for your baby"
                />
            </div>

            <div className="w-full max-w-sm">
                <ChildProfileDialog onSave={onSave} child={null}>
                    <Button size="lg" className="w-full text-base h-12 shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all">
                        <Baby className="mr-2 h-5 w-5" />
                        Create Child Profile
                    </Button>
                </ChildProfileDialog>
                <p className="text-center text-xs text-muted-foreground mt-4">
                    It only takes a moment to get started
                </p>
            </div>
        </div>
    );
}

function FeatureItem({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
    return (
        <Card className="border-none bg-secondary/30">
            <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-background flex items-center justify-center shadow-sm">
                    {icon}
                </div>
                <div className="text-left">
                    <h3 className="font-semibold text-sm">{title}</h3>
                    <p className="text-xs text-muted-foreground">{description}</p>
                </div>
            </CardContent>
        </Card>
    );
}
